import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-mention-picker>` says about itself.
 *
 * The thing worth documenting about this piece is what it will NOT do, because a person placing it
 * will otherwise expect all three: it does not fetch candidates, it does not filter them, and it
 * does not listen for a key. It draws a list and reports a choice.
 *
 * The keyboard is the surprising one, and the reason is the composer. Focus stays in the text box
 * the whole time somebody is typing a mention, so the arrows never reach this element; the composer
 * forwards them by calling `moveUp`/`moveDown` and names the active row on its own textarea with
 * `aria-activedescendant`. A picker that took focus to be navigable would send the next typed
 * character nowhere.
 */
const docs: ComponentDocs = {
  aliases: ['mention list', 'at mention', 'people picker', 'autocomplete list', 'typeahead list', 'suggestion list'],

  answers: [
    'offer people to mention while somebody types',
    'let somebody choose who they meant without leaving the text box',
    'show a shortlist under a text box',
    'say when nothing matched what was typed',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'candidates',
      type: 'json',
      default: [],
      description:
        'Who or what may be chosen, in the order they should read. Drawn exactly as given — this ' +
        'piece never filters or re-orders, because whoever fetched them applied the access rules and ' +
        'a second opinion here would disagree with theirs. Each entry is ' +
        '`{ kind, ref, label, hint? }`. Setting them puts the highlight back on the first row.',
    },
    {
      name: 'emptyLabel',
      attribute: 'empty-label',
      type: 'text',
      default: 'No matches.',
      description:
        'What is said when nothing matched. Worth setting: the default says a true thing about a list ' +
        'and nothing about what the reader could try instead.',
    },
  ],

  events: [
    {
      name: 'choose',
      carries: '{ kind, ref, label, hint? }',
      when:
        'Somebody clicked a row, or the surface called `chooseActive` because they pressed Enter. ' +
        'Nothing has been written into any text box — turning the choice into a mention is the ' +
        'surface’s job, and `insertMention` is the function that does it.',
    },
    {
      name: 'active-change',
      carries: '{ index: number, id: string | null }',
      when:
        'The highlighted row changed. The `id` is what a composer puts in its own ' +
        '`aria-activedescendant`, which is how a screen reader follows a highlight that is moving in ' +
        'an element the reader is not focused on.',
    },
  ],

  state: [
    {
      name: 'activeIndex',
      type: 'number',
      description: 'Which row Enter would choose. Always within the list; 0 when the list is empty.',
    },
    {
      name: 'active',
      type: 'the highlighted candidate, or null',
      description: 'What Enter would choose. Null when there is nothing to choose.',
    },
    {
      name: 'activeOptionId',
      type: 'text, or null',
      description:
        'The id of the highlighted row, for the composer’s `aria-activedescendant`. Null when there ' +
        'is nothing highlighted.',
    },
  ],

  slots: [],

  tokens: [
    { token: '--caos-color-surface', controls: 'Behind the list, so it reads as floating over the page.' },
    { token: '--caos-color-border', controls: 'The list’s edge.' },
    { token: '--caos-shadow-float', controls: 'The lift that separates the list from what is under it.' },
    { token: '--caos-color-text', controls: 'The names.' },
    { token: '--caos-color-text-subtle', controls: 'The second line that tells two identical names apart.' },
    { token: '--caos-color-text-muted', controls: 'The line shown when nothing matched.' },
    { token: '--caos-color-accent-fill', controls: 'Behind the highlighted row.' },
    { token: '--caos-color-accent-contrast', controls: 'The text drawn on that fill.' },
    { token: '--caos-font', controls: 'The typeface of the list.' },
    { token: '--caos-text-row', controls: 'The size of a name.' },
    { token: '--caos-text-label', controls: 'The size of the second line.' },
    { token: '--caos-space-1', controls: 'The inset around the rows.' },
    { token: '--caos-space-2', controls: 'Inside a row, and the gap between a name and its second line.' },
    { token: '--caos-radius-sm', controls: 'The roundness of the list and of the highlighted row.' },
  ],

  parts: [
    { part: 'list', is: 'the floating list itself' },
    { part: 'option', is: 'one choosable row' },
    { part: 'label', is: 'the name in a row' },
    { part: 'hint', is: 'the second line in a row', when: 'that candidate was given one' },
    { part: 'empty', is: 'the line shown when nothing matched', when: 'there are no candidates' },
  ],

  methods: [
    {
      name: 'moveDown',
      description:
        'Move the highlight down, wrapping at the bottom. What a composer calls when the reader ' +
        'presses the down arrow; this element hears no keys of its own.',
    },
    {
      name: 'moveUp',
      description: 'Move the highlight up, wrapping at the top.',
    },
    {
      name: 'chooseActive',
      description:
        'Choose the highlighted row, raising `choose`. Returns what was chosen, or null when there ' +
        'was nothing — so a composer can tell "Enter chose somebody" from "Enter should send".',
    },
  ],

  placing:
    'Directly under the text box somebody is typing into, as a floating list. It draws its own edge ' +
    'and lift, so it needs no container of its own; it is shown and hidden by setting `hidden` on it ' +
    'as the surface decides a mention is or is not being typed.',

  whenToUse:
    'Somebody is typing a mention into prose and needs to say who they meant without leaving the box. ' +
    'The list is short, it is driven by the keys they are already pressing, and it disappears when ' +
    'the mention is finished.',

  insteadUse: [
    {
      name: 'LookupField',
      when:
        'A record is being CHOSEN AS A VALUE rather than named inside a sentence. That is a field ' +
        'with a label, a stored id and a save behind it, none of which a mention has.',
    },
    {
      name: 'OverflowMenu',
      when:
        'The list is a set of actions to invoke rather than candidates to name. A menu takes focus ' +
        'and owns its keyboard, which is the opposite of what this does.',
    },
    {
      name: 'PicklistField',
      when:
        'The choice is one of a declared list of values rather than a person or a record. That list ' +
        'is fixed and known in advance; this one is fetched for every keystroke.',
    },
  ],

  accessibility:
    'It is a listbox that the text box beside it OWNS — the combobox pattern. It never takes focus ' +
    'and has no keyboard handler: the composer keeps focus, forwards the arrows through `moveUp`/' +
    '`moveDown`, and points at the highlighted row by putting `activeOptionId` into its own ' +
    '`aria-activedescendant`. Rows carry `role="option"` and `aria-selected`, so the highlight is ' +
    'announced as a selection rather than only drawn. Clicking a row is handled on `mousedown` with ' +
    'the default prevented, which is what stops the click from blurring the text box and losing the ' +
    'caret the insertion needs. The "nothing matched" line is `role="presentation"` rather than an ' +
    'option, because announcing it as a row tells a reader there is something to pick when there is ' +
    'not. Left to the author: naming the list, with `aria-label` — it is named "Mention suggestions" ' +
    'by default, which is right for one list on a page and wrong for two.',

  examples: [
    {
      key: 'people',
      label: 'People matching what was typed',
      summary: 'The second line is what tells two people with the same name apart.',
      placement: {
        component: 'caos-mention-picker',
        inputs: {
          'aria-label': 'Mention suggestions',
          candidates: [
            { kind: 'person', ref: '3f4c1b2a-5d6e-4f70-8a91-b2c3d4e5f607', label: 'Morgan Hale', hint: 'morgan.hale@example.test' },
            { kind: 'person', ref: '8b7a6c5d-4e3f-4210-9876-543210fedcba', label: 'Morgan Iyer', hint: 'm.iyer@example.test' },
            { kind: 'person', ref: '1a2b3c4d-5e6f-4708-9a0b-1c2d3e4f5061', label: 'Morgana Reyes', hint: 'morgana@example.test' },
          ],
        },
      },
    },
    {
      key: 'mixed',
      label: 'People and records together',
      summary:
        'A surface merges what the people lookup returned with what a record search returned; this ' +
        'piece draws the merged list in the order it was handed.',
      placement: {
        component: 'caos-mention-picker',
        inputs: {
          'aria-label': 'Mention suggestions',
          candidates: [
            { kind: 'person', ref: '3f4c1b2a-5d6e-4f70-8a91-b2c3d4e5f607', label: 'Morgan Hale', hint: 'morgan.hale@example.test' },
            { kind: 'record', ref: 'account/6d5c4b3a-2918-4706-b5a4-938271605f4e', label: 'Northgate Holdings', hint: 'Account' },
            { kind: 'record', ref: 'project/0f1e2d3c-4b5a-4968-8776-655443322110', label: 'Northgate retrofit', hint: 'Project' },
          ],
        },
      },
    },
    {
      key: 'empty',
      label: 'Nothing matched',
      summary: 'Said in the surface’s own words rather than left as a blank list.',
      placement: {
        component: 'caos-mention-picker',
        inputs: { 'aria-label': 'Mention suggestions', 'empty-label': 'Nobody here by that name.', candidates: [] },
      },
    },
  ],
};

export default docs;
